import React, { useState, useEffect } from 'react';
import { useBusiness } from '../contexts/BusinessContext';
import { supabase } from '../lib/supabase';
import { ChevronDown, ChevronRight, Calendar } from 'lucide-react';
import PermissionGuard from '../components/PermissionGuard';

// Interface for our item usage data
interface ItemUsage {
  item_id: string;
  item_name: string;
  total_quantity: number;
  total_cost: number;
  unit: string;
  supplier_name: string;
}

// Interface for supplier grouped data
interface SupplierGroup {
  supplier_name: string;
  total_cost: number;
  items: ItemUsage[];
}

// Date range options
type DateRange = 'this-month' | 'last-month' | 'all-time';

export default function SuppliesUsage() {
  const { selectedBusiness } = useBusiness();
  const [itemUsage, setItemUsage] = useState<ItemUsage[]>([]);
  const [supplierGroups, setSupplierGroups] = useState<SupplierGroup[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedSuppliers, setExpandedSuppliers] = useState<Record<string, boolean>>({});
  const [dateRange, setDateRange] = useState<DateRange>('this-month');
  const [isDateDropdownOpen, setIsDateDropdownOpen] = useState(false);

  // Get the human-readable date range label
  const getDateRangeLabel = (range: DateRange): string => {
    switch (range) {
      case 'this-month': return 'This Month';
      case 'last-month': return 'Last Month';
      case 'all-time': return 'All Time';
      default: return 'This Month';
    }
  };

  // Function to fetch stock history data for received items
  const fetchStockUsage = async () => {
    if (!supabase) {
      setIsLoading(false);
      setError("Supabase client not initialized");
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Set up the base query
      let query = supabase
        .from('stock_history_with_details')
        .select('item_id, item_name, item_unit, quantity_change, price, transaction_type, supplier_name, created_at')
        .eq('transaction_type', 'Received');

      // Add date range filters
      if (dateRange !== 'all-time') {
        const now = new Date();
        let startDate: Date;
        let endDate: Date;
        
        if (dateRange === 'this-month') {
          // First day of current month
          startDate = new Date(now.getFullYear(), now.getMonth(), 1);
          // Last day of current month
          endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
        } else { // last-month
          // First day of previous month
          startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
          // Last day of previous month
          endDate = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59, 999);
        }
        
        query = query
          .gte('created_at', startDate.toISOString())
          .lte('created_at', endDate.toISOString());
      }

      // Execute the query
      const { data, error } = await query;

      if (error) {
        console.error('Error fetching stock history:', error);
        throw error;
      }

      if (!data || data.length === 0) {
        console.log('No stock history data found');
        setItemUsage([]);
        setSupplierGroups([]);
        setIsLoading(false);
        return;
      }

      console.log('Stock history data:', data);

      // Process data to calculate usage by item
      const usageByItem = new Map<string, ItemUsage>();

      data.forEach(item => {
        const itemId = item.item_id;
        const itemName = item.item_name || 'Unknown Item';
        const quantity = Number(item.quantity_change) || 0;
        const price = Number(item.price) || 0;
        const unit = item.item_unit || 'unit';
        const supplierName = item.supplier_name || 'Unknown Supplier';
        const cost = quantity * price;

        const itemKey = `${itemId}-${supplierName}`;

        if (usageByItem.has(itemKey)) {
          const existing = usageByItem.get(itemKey)!;
          existing.total_quantity += quantity;
          existing.total_cost += cost;
          usageByItem.set(itemKey, existing);
        } else {
          usageByItem.set(itemKey, {
            item_id: itemId,
            item_name: itemName,
            total_quantity: quantity,
            total_cost: cost,
            unit,
            supplier_name: supplierName
          });
        }
      });

      // Convert map to array and sort by total cost (highest first)
      const usageArray = Array.from(usageByItem.values())
        .sort((a, b) => b.total_cost - a.total_cost);

      setItemUsage(usageArray);

      // Group by supplier
      const supplierMap = new Map<string, SupplierGroup>();
      
      usageArray.forEach(item => {
        const supplierName = item.supplier_name;
        
        if (supplierMap.has(supplierName)) {
          const group = supplierMap.get(supplierName)!;
          group.total_cost += item.total_cost;
          group.items.push(item);
          supplierMap.set(supplierName, group);
        } else {
          supplierMap.set(supplierName, {
            supplier_name: supplierName,
            total_cost: item.total_cost,
            items: [item]
          });
        }
      });
      
      // Convert to array and sort by total cost
      const supplierArray = Array.from(supplierMap.values())
        .sort((a, b) => b.total_cost - a.total_cost);
      
      setSupplierGroups(supplierArray);
    } catch (err) {
      console.error('Error fetching stock usage:', err);
      setError('Failed to load supplies usage data');
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch data on component mount or when business/date range changes
  useEffect(() => {
    fetchStockUsage();
  }, [selectedBusiness?.id, dateRange]);

  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  // Toggle supplier expansion
  const toggleSupplier = (supplierName: string) => {
    setExpandedSuppliers(prev => ({
      ...prev,
      [supplierName]: !prev[supplierName]
    }));
  };

  // Handle clicks outside of dropdowns
  const handleOutsideClick = () => {
    setIsDateDropdownOpen(false);
  };

  // Calculate total cost of all items
  const totalCost = supplierGroups.reduce((sum, group) => sum + group.total_cost, 0);

  // Render the mobile card view for a supplier
  const renderMobileSupplierCard = (group: SupplierGroup) => {
    return (
      <div key={group.supplier_name} className="mb-4 border border-gray-200 rounded-lg overflow-hidden">
        <button 
          className="w-full flex items-center justify-between bg-gray-50 px-4 py-3 hover:bg-gray-100 transition-colors text-left"
          onClick={() => toggleSupplier(group.supplier_name)}
        >
          <div className="flex items-center">
            {expandedSuppliers[group.supplier_name] ? (
              <ChevronDown className="h-4 w-4 mr-2 flex-shrink-0" />
            ) : (
              <ChevronRight className="h-4 w-4 mr-2 flex-shrink-0" />
            )}
            <div className="truncate">
              <span className="font-medium text-gray-900">{group.supplier_name}</span>
              <div className="text-xs text-gray-500">{group.items.length} items</div>
            </div>
          </div>
          <div className="font-semibold text-right text-gray-900">{formatCurrency(group.total_cost)}</div>
        </button>
        
        {expandedSuppliers[group.supplier_name] && (
          <div className="border-t border-gray-200 divide-y divide-gray-200">
            {group.items.map((item) => (
              <div key={`${item.supplier_name}-${item.item_id}`} className="px-4 py-3 bg-white">
                <div className="text-sm font-medium text-gray-800">{item.item_name}</div>
                <div className="flex justify-between items-center mt-1">
                  <div className="text-xs text-gray-500">{item.total_quantity} {item.unit}</div>
                  <div className="text-sm font-medium text-gray-900">{formatCurrency(item.total_cost)}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <PermissionGuard permission="suppliesUsage.pageAccess">
      <div className="bg-gray-50 rounded-lg p-4 sm:p-6 min-h-screen" onClick={handleOutsideClick}>
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-800">Supplies Usage Report</h1>
          <p className="text-gray-500 mt-1 text-sm sm:text-base">Track and analyze supplies consumption</p>
        </div>
        
        {/* Date range filter */}
        <div className="mt-4 md:mt-0 relative w-full md:w-auto">
          <button 
            className="w-full md:w-auto bg-white rounded-lg shadow-sm border border-gray-200 px-4 py-2 flex items-center justify-between md:justify-start space-x-2 text-gray-700 hover:bg-gray-50 transition-colors"
            onClick={(e) => {
              e.stopPropagation();
              setIsDateDropdownOpen(!isDateDropdownOpen);
            }}
          >
            <div className="flex items-center">
              <Calendar className="h-4 w-4 text-gray-500 mr-2" />
              <span>{getDateRangeLabel(dateRange)}</span>
            </div>
            <ChevronDown className={`h-4 w-4 transition-transform ${isDateDropdownOpen ? 'rotate-180' : ''}`} />
          </button>
          
          {isDateDropdownOpen && (
            <div className="absolute right-0 left-0 md:left-auto mt-2 md:w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-10">
              {[
                { value: 'this-month', label: 'This Month' },
                { value: 'last-month', label: 'Last Month' },
                { value: 'all-time', label: 'All Time' }
              ].map((option) => (
                <button
                  key={option.value}
                  className={`w-full text-left px-4 py-3 sm:py-2 text-sm ${
                    dateRange === option.value 
                      ? 'bg-gray-100 text-gray-900 font-medium' 
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setDateRange(option.value as DateRange);
                    setIsDateDropdownOpen(false);
                  }}
                >
                  {option.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
      
      {/* Supplies Usage Report */}
      <div className="bg-white rounded-xl shadow-md border border-gray-100 p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-2">
          <h2 className="text-lg font-semibold text-gray-800">
            Supplies Purchased
            <span className="ml-2 text-sm font-normal text-gray-500">({getDateRangeLabel(dateRange)})</span>
          </h2>
          <div className="font-bold text-green-600 text-lg">
            {isLoading ? 'Loading...' : formatCurrency(totalCost)}
          </div>
        </div>

        {isLoading ? (
          <div className="h-60 flex items-center justify-center">
            <p className="text-gray-400">Loading data...</p>
          </div>
        ) : error ? (
          <div className="h-60 flex items-center justify-center">
            <p className="text-red-500">{error}</p>
          </div>
        ) : supplierGroups.length === 0 ? (
          <div className="h-60 flex items-center justify-center">
            <p className="text-gray-400">No supplies usage data found for the selected period</p>
          </div>
        ) : (
          <>
            {/* Mobile view - Card based layout */}
            <div className="block sm:hidden">
              {supplierGroups.map(renderMobileSupplierCard)}
            </div>
            
            {/* Desktop view - Table layout */}
            <div className="hidden sm:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Supplier / Item
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Quantity
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Total Cost
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {supplierGroups.map((group) => (
                    <React.Fragment key={group.supplier_name}>
                      {/* Supplier row */}
                      <tr 
                        className="bg-gray-50 hover:bg-gray-100 cursor-pointer"
                        onClick={() => toggleSupplier(group.supplier_name)}
                      >
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 flex items-center">
                          {expandedSuppliers[group.supplier_name] ? (
                            <ChevronDown className="h-4 w-4 mr-2" />
                          ) : (
                            <ChevronRight className="h-4 w-4 mr-2" />
                          )}
                          {group.supplier_name}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {group.items.length} items
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900 text-right">
                          {formatCurrency(group.total_cost)}
                        </td>
                      </tr>
                      
                      {/* Item rows - only shown when supplier is expanded */}
                      {expandedSuppliers[group.supplier_name] && group.items.map((item) => (
                        <tr key={`${item.supplier_name}-${item.item_id}`} className="hover:bg-gray-50 bg-white">
                          <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-900 pl-12">
                            {item.item_name}
                          </td>
                          <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-500">
                            {item.total_quantity} {item.unit}
                          </td>
                          <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-900 text-right">
                            {formatCurrency(item.total_cost)}
                          </td>
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
    </PermissionGuard>
  );
} 